import { useAuth } from '@/context/AuthContext';
import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';

const ArrearsLegalData = ({ period, badDebtPercentages, billings, hideLegalRows = false }) => {
    const { apiFetch } = useAuth();
    const [legalArrearsData, setLegalArrearsData] = useState([]);
    const [arrearsData, setArrearsData] = useState();
    const [prevArrearsData, setPrevArrearsData] = useState({});
    const [billingData, setBillingData] = useState([]);
    const { percentageCurrent, percentageYTD, percentagePrev } = badDebtPercentages;
    const { monthlyBillingsCurrent, monthlyBillingsYTD, monthlyBillingsPrev } = billings;
    const [legalMattersByPeriod, setLegalMattersByPeriod] = useState({ current: null, previous: null });
    const [paidToSellerAmounts, setPaidToSellerAmounts] = useState({});

    function getMonthYear(period) {
        if (!period) return { monthName: '', year: '', prevYear: '' };
        const periodStr = period.toString().padStart(6, '0');
        if (periodStr.length !== 6) return { monthName: '', year: '', prevYear: '' };
        const year = periodStr.substring(0, 4);
        const prevYear = (parseInt(year, 10) - 1).toString();
        const month = periodStr.substring(4, 6);
        const monthNames = [
            '', 'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        const monthIndex = parseInt(month, 10);
        return {
            monthName: monthNames[monthIndex],
            year,
            prevYear
        };
    }

    const { monthName, year, prevYear } = getMonthYear(period);

    useEffect(() => {
        if (!period) return;
        let isCancelled = false;

        const fetchAll = async () => {
            try {
                const results = await Promise.allSettled([
                    apiFetch(`/api/Mda/GetLegalArrearsMatters?Period=${period}`).then(r => r.json()),
                    apiFetch(`/api/Mda/GetArrearsForPeriod?Period=${period}`).then(r => r.json()),
                    apiFetch(`/api/Legal/OtherLegalMatters/CountsByPeriod?period=${period}`).then(r => r.json()),
                    apiFetch(`/api/Mda/SumInclusiveAmountByPeriod?period=${period}`).then(r => r.json()),
                    apiFetch(`/api/Mda/GetArrearsForLast12Periods?period=${period}`).then(r => r.json()),
                    apiFetch(`/api/Leasing/PaidToSeller13MonthRange?period=${period}`).then(r => r.json())
                ]);

                if (isCancelled) return;

                const [legalResult, arrearsResult, legalByPeriodResult, billingsResult, prevArrearsResult, paidResult] = results;

                if (legalResult.status === 'fulfilled') {
                    setLegalArrearsData(legalResult.value);
                } else {
                    Sentry.captureException(legalResult.reason);
                    console.error('GetLegalArrearsMatters failed:', legalResult.reason);
                }

                if (arrearsResult.status === 'fulfilled') {
                    setArrearsData(arrearsResult.value);
                } else {
                    Sentry.captureException(arrearsResult.reason);
                    console.error('GetArrearsForPeriod failed:', arrearsResult.reason);
                }

                if (legalByPeriodResult.status === 'fulfilled' && legalByPeriodResult.value.success) {
                    setLegalMattersByPeriod(legalByPeriodResult.value.data);
                } else if (legalByPeriodResult.status === 'rejected') {
                    Sentry.captureException(legalByPeriodResult.reason);
                    console.error('CountsByPeriod failed:', legalByPeriodResult.reason);
                }

                if (billingsResult.status === 'fulfilled' && billingsResult.value.success) {
                    setBillingData(billingsResult.value.data);
                } else if (billingsResult.status === 'rejected') {
                    Sentry.captureException(billingsResult.reason);
                    console.error('SumInclusiveAmountByPeriod failed:', billingsResult.reason);
                }

                if (prevArrearsResult.status === 'fulfilled' && prevArrearsResult.value.success) {
                    setPrevArrearsData(prevArrearsResult.value.data);
                } else if (prevArrearsResult.status === 'rejected') {
                    Sentry.captureException(prevArrearsResult.reason);
                    console.error('GetArrearsForLast12Periods failed:', prevArrearsResult.reason);
                }

                if (paidResult.status === 'fulfilled' && paidResult.value.success) {
                    setPaidToSellerAmounts(paidResult.value.data);
                } else if (paidResult.status === 'rejected') {
                    Sentry.captureException(paidResult.reason);
                    console.error('PaidToSeller13MonthRange failed:', paidResult.reason);
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
            }
        };

        fetchAll();

        return () => {
            isCancelled = true;
        };
    }, [apiFetch, period]);

    const calculateTotalPeriodArrears = () => {
        if (!arrearsData) return 0;

        return Object.keys(arrearsData)
            .filter(propertyName => propertyName !== "Unity Centre")
            .reduce((total, propertyName) => {
                return total + (arrearsData[propertyName].periodArrears || 0);
            }, 0);
    };

    const calculateTotalPreviousYearArrears = () => {
        if (!arrearsData) return 0;

        return Object.keys(arrearsData)
            .filter(propertyName => propertyName !== "Unity Centre")
            .reduce((total, propertyName) => {
                return total + (arrearsData[propertyName].previousYearArrears || 0);
            }, 0);
    };

    const arrearsBillingsMonthly = () => {
        const billings = Number(monthlyBillingsCurrent);
        if (!billings) return 0;
        return calculateTotalPeriodArrears() / billings;
    };

    const arrearsBillingsYTD = () => {
        const billings = Number(monthlyBillingsYTD);
        if (!billings) return 0;
        return calculateTotalPeriodArrears() / billings;
    };

    const arrearsBillingsPrev = () => {
        const billings = Number(monthlyBillingsPrev);
        if (!billings) return 0;
        return (calculateTotalPreviousYearArrears() / billings);
    };

    const yearlyBillingsCurrent = () => {
        if (!billingData || !period || !prevArrearsData) return 0;

        function periodToLabel(period) {
            const periodStr = period.toString().padStart(6, '0');
            const year = periodStr.substring(0, 4);
            const month = periodStr.substring(4, 6);
            const monthNames = [
                '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'
            ];
            const monthIndex = parseInt(month, 10);
            return `${monthNames[monthIndex]} ${year}`;
        }

        const label = periodToLabel(period);
        const arrears = prevArrearsData[label] || 0;

        const totalInclusiveCurrent = sumBillingsFor12Months(period, billingData);
        const taxExclusive = totalInclusiveCurrent / 1.15;

        const paidToSeller = paidToSellerAmounts && paidToSellerAmounts[period] ? paidToSellerAmounts[period] : 0;
        const adjustedArrears = arrears - paidToSeller;

        const percent = (adjustedArrears / taxExclusive) * 100;
        return roundToTenth(percent);
    };

    const yearlyBillingsPrev = () => {
        if (!billingData || !period || !prevArrearsData) return 0;
        const { prevYear } = getMonthYear(period);
        const prevDecemberPeriod = `${prevYear}12`;

        function periodToLabel(period) {
            const periodStr = period.toString().padStart(6, '0');
            const year = periodStr.substring(0, 4);
            const month = periodStr.substring(4, 6);
            const monthNames = [
                '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'
            ];
            const monthIndex = parseInt(month, 10);
            return `${monthNames[monthIndex]} ${year}`;
        }

        const label = periodToLabel(prevDecemberPeriod);
        const arrearsPrev = prevArrearsData[label] || 0;

        const totalInclusivePrev = sumBillingsFor12Months(Number(prevDecemberPeriod), billingData);
        const taxExclusivePrev = totalInclusivePrev / 1.15;

        const paidToSellerPrev = paidToSellerAmounts && paidToSellerAmounts[prevDecemberPeriod] ? paidToSellerAmounts[prevDecemberPeriod] : 0;
        const adjustedArrearsPrev = arrearsPrev - paidToSellerPrev;

        const percent = (adjustedArrearsPrev / taxExclusivePrev) * 100;
        return roundToTenth(percent);
    }

    function roundToTenth(value) {
        return Math.round(value * 10) / 10;
    }

    function formatNumberWithSpace(value) {
        if (typeof value !== 'number') value = Number(value) || 0;
        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }

    function sumBillingsFor12Months(period, billingData) {
        const periodNum = Number(period);
        const sorted = [...billingData].sort((a, b) => b.period - a.period);
        const startIdx = sorted.findIndex(item => Number(item.period) === periodNum);
        if (startIdx === -1) return 0;
        const slice = sorted.slice(startIdx, startIdx + 12);
        return slice.reduce((sum, item) => sum + (item.totalInclusiveAmount || 0), 0);
    }

    return (
        <div>
            <table className="arrears-table-container">
                <thead>
                    <tr>
                        <th>Arrears and Legal Statistics</th>
                        <th>
                            Month<br />
                            {monthName} {year}
                        </th>
                        <th>
                            Year to Date<br />
                            {monthName} {year}
                        </th>
                        <th>
                            Year Ended<br />
                            {`December ${prevYear}`}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Bad debts (% of income)</td>
                        <td>{percentageCurrent}</td>
                        <td>{percentageYTD}</td>
                        <td>{percentagePrev}</td>
                    </tr>
                    <tr>
                        <td>Arrears (% of monthly billings)</td>
                        <td>{(arrearsBillingsMonthly() * 100).toFixed(1)}%</td>
                        <td>{(arrearsBillingsYTD() * 100).toFixed(1)}%</td>
                        <td>{(arrearsBillingsPrev() * 100).toFixed(1)}%</td>
                    </tr>
                    <tr>
                        <td>Arrears (% of yearly billings)</td>
                        <td>-</td>
                        <td>{yearlyBillingsCurrent()}%</td>
                        <td>{yearlyBillingsPrev()}%</td>
                    </tr>
                    {!hideLegalRows && (
                        <>
                            <tr>
                                <td>Legal arrears matters</td>
                                <td>-</td>
                                <td>{legalMattersByPeriod.current ? formatNumberWithSpace(legalMattersByPeriod.current.legalArrearsMattersCount) : ''}</td>
                                <td>{legalMattersByPeriod.previous ? formatNumberWithSpace(legalMattersByPeriod.previous.legalArrearsMattersCount) : ''}</td>
                            </tr>
                            <tr>
                                <td>Other legal matters</td>
                                <td>-</td>
                                <td>{legalMattersByPeriod.current ? formatNumberWithSpace(legalMattersByPeriod.current.otherLegalMattersCount) : ''}</td>
                                <td>{legalMattersByPeriod.previous ? formatNumberWithSpace(legalMattersByPeriod.previous.otherLegalMattersCount) : ''}</td>
                            </tr>
                        </>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default ArrearsLegalData;